/**
 * O cadastro dos tanques — a primeira tela do sistema que fala de peixe.
 *
 * DUAS DECISÕES QUE VALE ENTENDER AQUI:
 *
 * 1. INATIVAR NÃO APAGA. O tanque sai das listas de escolha e continua
 *    existindo com todo o histórico dos ciclos que passaram por ele. É por
 *    isso que o botão diz «Tirar de uso» e não «Excluir»: o app não sabe
 *    apagar tanque, e o banco não deixaria nem se ele tentasse.
 *
 * 2. A ÁREA APARECE EM METRO E EM HECTARE, sempre as duas. A fazenda mede o
 *    tanque em metro quadrado e conversa densidade em tonelada por hectare —
 *    obrigar a cabeça de alguém a dividir por dez mil no meio do trabalho é
 *    onde nasce erro de ordem de grandeza.
 */

import { useCallback, useEffect, useState } from 'react'
import { numeroPtBr } from '../dominio/unidades'
import {
  listarTanques, salvarTanque,
  type Tanque, type TanqueParaSalvar, type UsoDeTanque,
} from '../acesso/api'

const USOS: readonly UsoDeTanque[] = ['alevinagem', 'juvenil', 'engorda', 'misto']

/** Sem acento, sem maiúscula, sem espaço sobrando — só para comparar. */
function achatar(texto: string): string {
  return texto
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

/** O setor já cadastrado que casa com o que se digitou, ou nulo. */
function jaExiste(digitado: string, setores: string[]): string | null {
  if (digitado.trim() === '') return null
  const alvo = achatar(digitado)
  return setores.find((s) => achatar(s) === alvo) ?? null
}

/**
 * O setor que vai para o banco.
 *
 * Escolher da lista devolve o que foi escolhido. Criar um novo devolve o nome
 * digitado — MENOS quando ele já existe com outra grafia, e aí vale o que já
 * estava lá. É aqui que «Norte», «norte» e «  Norte » param de virar três.
 */
function setorEscolhido(setor: string, novo: string, setores: string[]): string | null {
  if (setor === '') return null
  if (setor !== '__novo__') return setor
  const igual = jaExiste(novo, setores)
  if (igual !== null) return igual
  return novo.trim() === '' ? null : novo.trim()
}

export function Tanques() {
  const [tanques, setTanques] = useState<Tanque[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  /** `null` = nada aberto; `'novo'` = cadastro; um tanque = correção dele. */
  const [editando, setEditando] = useState<Tanque | 'novo' | null>(null)

  const recarregar = useCallback(async () => {
    try {
      setTanques(await listarTanques())
      setErro(null)
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  async function aoAlternarUso(tanque: Tanque) {
    const tirando = tanque.ativo
    if (
      tirando &&
      !confirm(
        `Tirar o ${tanque.codigo} de uso?\n\n` +
          'Ele some das listas de escolha. O histórico dos ciclos que já ' +
          'passaram por ele continua inteiro, e dá para voltar quando quiser.',
      )
    ) {
      return
    }
    try {
      // Montado campo a campo de propósito: espalhar o tanque inteiro
      // arrastaria junto os campos da 005 que esta tela ainda não edita.
      await salvarTanque({
        id: tanque.id,
        codigo: tanque.codigo,
        nome: tanque.nome,
        area_m2: tanque.area_m2,
        profundidade_media_m: tanque.profundidade_media_m,
        uso_habitual: tanque.uso_habitual,
        observacao: tanque.observacao,
        ativo: !tanque.ativo,
        // O setor viaja junto: sem isto, inativar um tanque apagaria o setor
        // dele, porque o UPDATE do servidor grava o que recebe.
        setor: tanque.setor,
        // A CARGA-ALVO NÃO VAI, de propósito. O servidor só a toca quando ela
        // vem — e ligar ou desligar o tanque não é assunto dela.
      })
      await recarregar()
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'não deu para salvar')
    }
  }

  if (carregando) return <p className="aviso">Carregando…</p>

  const emUso = tanques.filter((t) => t.ativo)
  const areaEmUso = emUso.reduce((soma, t) => soma + t.area_m2, 0)
  const areaTotal = tanques.reduce((soma, t) => soma + t.area_m2, 0)

  return (
    <div className="tanques">
      {erro && <p className="aviso-erro">{erro}</p>}

      <section className="bloco">
        <h2>Tanques</h2>
        <p className="secundario">
          {tanques.length} cadastrados, {emUso.length} em uso ·{' '}
          <strong>{emHectares(areaEmUso)}</strong> de lâmina em operação
          {areaTotal > areaEmUso && <> , de {emHectares(areaTotal)} no total</>}
        </p>

        {tanques.length === 0 ? (
          <p className="secundario">Nenhum tanque cadastrado ainda.</p>
        ) : (
          <ul className="lista">
            {tanques.map((tanque) => (
              <li key={tanque.id} className={tanque.ativo ? 'linha' : 'linha inativa'}>
                <div>
                  <strong>{tanque.codigo}</strong>
                  {tanque.nome !== tanque.codigo && (
                    <span className="etiqueta">{tanque.nome}</span>
                  )}
                  {tanque.uso_habitual && (
                    <span className="etiqueta">{tanque.uso_habitual}</span>
                  )}
                  {!tanque.ativo && <span className="etiqueta etiqueta-fria">fora de uso</span>}
                  <div className="secundario">
                    {emMetros(tanque.area_m2)} · {emHectares(tanque.area_m2)}
                    {tanque.profundidade_media_m !== null && (
                      <> · {numero(tanque.profundidade_media_m)} m de profundidade</>
                    )}
                    {tanque.carga_final_alvo_t_ha !== null && (
                      <> · carga alvo {numero(tanque.carga_final_alvo_t_ha)} t/ha</>
                    )}
                  </div>
                  {tanque.observacao && <div className="secundario">{tanque.observacao}</div>}
                </div>
                <div className="acoes">
                  <button type="button" className="discreto" onClick={() => setEditando(tanque)}>
                    Corrigir
                  </button>
                  <button
                    type="button"
                    className="discreto"
                    onClick={() => void aoAlternarUso(tanque)}
                  >
                    {tanque.ativo ? 'Tirar de uso' : 'Voltar ao uso'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {editando === null ? (
          <button type="button" className="discreto" onClick={() => setEditando('novo')}>
            + Cadastrar tanque
          </button>
        ) : (
          <Formulario
            // Os setores saem dos próprios tanques: quem já existe é a lista.
            // Não há cadastro de setor a manter, e não há como a lista ficar
            // desencontrada do que está gravado.
            setores={[...new Set(
              tanques.map((t) => t.setor).filter((x): x is string => x !== null && x !== ''),
            )].sort((a, b) => a.localeCompare(b, 'pt-BR'))}
            tanque={editando === 'novo' ? null : editando}
            aoFechar={() => setEditando(null)}
            aoSalvar={() => {
              setEditando(null)
              void recarregar()
            }}
          />
        )}
      </section>
    </div>
  )
}

function Formulario({
  tanque, setores, aoFechar, aoSalvar,
}: {
  tanque: Tanque | null
  aoFechar: () => void
  aoSalvar: () => void
  /** Os setores que já existem, para escolher em vez de digitar. */
  setores: string[]
}) {
  const [codigo, setCodigo] = useState(tanque?.codigo ?? '')
  const [nome, setNome] = useState(tanque && tanque.nome !== tanque.codigo ? tanque.nome : '')
  // OS CAMPOS NASCEM EM pt-BR, e isto não é cosmético. `String(1.8)` é «1.8»,
  // e o leitor de números do sistema recusa ponto como decimal — então corrigir
  // QUALQUER outra coisa num tanque de 1,8 m gravava a profundidade vazia,
  // calado. Achado em 23/09/2026, ao pôr a carga-alvo aqui, que cairia no
  // mesmo buraco.
  const [area, setArea] = useState(tanque ? paraCampo(tanque.area_m2) : '')
  const [profundidade, setProfundidade] = useState(paraCampo(tanque?.profundidade_media_m ?? null))
  const [carga, setCarga] = useState(paraCampo(tanque?.carga_final_alvo_t_ha ?? null))
  const [uso, setUso] = useState<UsoDeTanque | ''>(tanque?.uso_habitual ?? '')
  const [setor, setSetor] = useState(tanque?.setor ?? '')
  const [setorNovo, setSetorNovo] = useState('')
  const [observacao, setObservacao] = useState(tanque?.observacao ?? '')
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)

  // «12.000» m² é doze mil, não doze. Padrão do sistema.
  const areaNumero = numeroPtBr(area) ?? NaN
  const profundidadeM = numeroPtBr(profundidade)
  const cargaTHa = numeroPtBr(carga)

  return (
    <form
      className="nova-pessoa"
      onSubmit={(evento) => {
        evento.preventDefault()
        setErro(null)

        // CAMPO PREENCHIDO QUE NÃO VIROU NÚMERO NÃO VAI COMO VAZIO. Ir como
        // vazio apagaria o valor que estava gravado — o pior tipo de erro,
        // porque o formulário diz «salvo» e o dado some.
        if (profundidade.trim() !== '' && profundidadeM === null) {
          setErro('A profundidade não é um número. Use vírgula para o decimal: 1,8.')
          return
        }
        if (carga.trim() !== '' && cargaTHa === null) {
          setErro('A carga final alvo não é um número. Use vírgula para o decimal: 6,8.')
          return
        }

        setSalvando(true)

        const dados: TanqueParaSalvar = {
          ...(tanque ? { id: tanque.id } : {}),
          codigo: codigo.trim().toUpperCase(),
          nome: nome.trim(),
          area_m2: areaNumero,
          profundidade_media_m: profundidadeM,
          // Do formulário ela vai SEMPRE — vazia quer dizer «apagar», e é a
          // única tela que pode dizer isso.
          carga_final_alvo_t_ha: cargaTHa,
          uso_habitual: uso === '' ? null : uso,
          ativo: tanque?.ativo ?? true,
          observacao: observacao.trim() === '' ? null : observacao.trim(),
          setor: setorEscolhido(setor, setorNovo, setores),
        }

        salvarTanque(dados)
          .then(aoSalvar)
          .catch((falha: unknown) => {
            setErro(falha instanceof Error ? falha.message : 'não deu para salvar')
          })
          .finally(() => setSalvando(false))
      }}
    >
      <label className="campo">
        <span className="campo-rotulo">Código</span>
        <input
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="T-10"
          autoFocus
        />
      </label>

      <label className="campo">
        <span className="campo-rotulo">Nome (opcional)</span>
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder={codigo.trim() === '' ? 'o próprio código' : codigo.trim().toUpperCase()}
        />
      </label>

      <label className="campo">
        <span className="campo-rotulo">Área, em metros quadrados</span>
        <input
          value={area}
          onChange={(e) => setArea(e.target.value)}
          inputMode="decimal"
          placeholder="9500"
        />
        {/* A conversão aparece enquanto se digita: é o momento em que um zero a
            mais ou a menos ainda dá para ver. */}
        {Number.isFinite(areaNumero) && areaNumero > 0 && (
          <span className="secundario">{emHectares(areaNumero)}</span>
        )}
      </label>

      <label className="campo">
        <span className="campo-rotulo">Profundidade média, em metros (opcional)</span>
        <input
          value={profundidade}
          onChange={(e) => setProfundidade(e.target.value)}
          inputMode="decimal"
          placeholder="1,8"
        />
      </label>

      <label className="campo">
        <span className="campo-rotulo">Carga final alvo, em t/ha (opcional)</span>
        <input
          value={carga}
          onChange={(e) => setCarga(e.target.value)}
          inputMode="decimal"
          placeholder="8"
        />
        {/* Em toneladas enquanto se digita, pelo mesmo motivo da área: é onde
            um zero a mais ainda aparece. */}
        {Number.isFinite(areaNumero) && areaNumero > 0 && cargaTHa !== null && cargaTHa > 0 && (
          <span className="secundario">
            ≈ {numero((cargaTHa * areaNumero) / 10_000)} t de peixe nesta represa no fim do ciclo
          </span>
        )}
        <span className="campo-dica">
          A biomassa pretendida no fim do ciclo. É com ela que a Movimentação calcula quantos
          peixes cabem aqui. Em branco, a barra de lotação não aparece.
        </span>
      </label>

      <label className="campo">
        <span className="campo-rotulo">Uso habitual (opcional)</span>
        <select value={uso} onChange={(e) => setUso(e.target.value as UsoDeTanque | '')}>
          <option value="">não definido</option>
          {USOS.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
        <span className="secundario">
          É só a etiqueta do costume. O sistema nunca muda a fase de um ciclo por
          causa dela.
        </span>
      </label>

      {/* O SETOR, E A CILADA DA GRAFIA.
          O gestor pediu: «não podemos cair na cilada de cadastrar o mesmo
          setor com diferentes escritas». Por isso o caminho normal é ESCOLHER
          de uma lista, e criar é a exceção — e o nome criado passa por uma
          comparação sem acento e sem maiúscula contra os que já existem. Quem
          digitar «norte» onde já há «Norte» fica com «Norte». */}
      <label className="campo">
        <span className="campo-rotulo">Setor</span>
        <select value={setor} onChange={(e) => setSetor(e.target.value)}>
          <option value="">sem setor</option>
          {setores.map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
          <option value="__novo__">+ novo setor…</option>
        </select>
        <span className="campo-dica">
          Serve para a tela agrupar os tanques. Com um setor só, ela nem aparece.
        </span>
      </label>

      {setor === '__novo__' && (
        <label className="campo">
          <span className="campo-rotulo">Nome do setor novo</span>
          <input
            value={setorNovo}
            onChange={(e) => setSetorNovo(e.target.value)}
            placeholder="Norte"
          />
          {jaExiste(setorNovo, setores) !== null && (
            <span className="campo-dica">
              Já existe <strong>{jaExiste(setorNovo, setores)}</strong> — vai ser
              usado esse, para não virarem dois setores.
            </span>
          )}
        </label>
      )}

      <label className="campo">
        <span className="campo-rotulo">Observação (opcional)</span>
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="drenagem difícil, fundo de barro…"
        />
      </label>

      {erro && <p className="aviso-erro">{erro}</p>}

      <div className="acoes">
        <button type="submit" disabled={salvando}>
          {salvando ? 'Salvando…' : tanque ? 'Salvar correção' : 'Cadastrar'}
        </button>
        <button type="button" className="discreto" onClick={aoFechar}>
          Cancelar
        </button>
      </div>
    </form>
  )
}

/** Um número gravado, escrito como o campo o lê de volta: «1,8», nunca «1.8». */
function paraCampo(valor: number | null): string {
  return valor === null
    ? ''
    : valor.toLocaleString('pt-BR', { useGrouping: false, maximumFractionDigits: 3 })
}

function emMetros(m2: number): string {
  return `${numero(m2)} m²`
}

function emHectares(m2: number): string {
  return `${numero(m2 / 10000, 2)} ha`
}

function numero(valor: number, casas = 1): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: casas })
}
